import { getSupabaseClient } from '../supabaseClient'

/**
 * Resolves what the signed-in user is allowed to reach.
 *
 * Every query here runs as the authenticated user through the ordinary client,
 * so Row Level Security decides what comes back. There is no service-role key
 * in browser code and no Edge Function involved: resolving your own access is
 * an ordinary read, and routing it through a privileged path would mean the
 * answer no longer came from the policies that actually guard the data.
 *
 * The result type is deliberately a discriminated union rather than
 * `Access | null`. "You have no membership", "the database refused this" and
 * "the network failed" are three different situations, and collapsing them into
 * a falsy value is how a permission failure quietly becomes an onboarding
 * screen. Section 8 of the M5 brief calls this out; it is also just true.
 */

export type OrganisationRole = 'owner' | 'admin' | 'viewer'

export interface AccessMembership {
  readonly membershipId: string
  readonly organisationId: string
  readonly organisationName: string
  readonly organisationStatus: 'active' | 'suspended'
  readonly role: OrganisationRole
  readonly canViewFinancials: boolean
  /** The employee this login is, set explicitly by an administrator, or null. */
  readonly employeeId: string | null
}

export interface AccessProfile {
  readonly userId: string
  readonly email: string
  readonly fullName: string | null
}

export interface Access {
  readonly profile: AccessProfile
  readonly memberships: readonly AccessMembership[]
  readonly isPlatformAdmin: boolean
}

export type AccessResult =
  | { readonly kind: 'ok'; readonly access: Access }
  /** The database refused the read. Never the same as having no membership. */
  | { readonly kind: 'denied' }
  /** Reached the database, but the user's own profile row is absent. */
  | { readonly kind: 'incomplete' }
  | { readonly kind: 'error'; readonly reason: 'network' | 'unknown' }

interface MembershipRow {
  id: string
  organisation_id: string
  role: string
  can_view_financials: boolean
  employee_id: string | null
  organisations: { id: string; name: string; status: string } | null
}

/** Postgres codes that mean "the policy said no", not "there is nothing here". */
const DENIED_CODES = new Set(['42501', 'PGRST301', '42P01'])

function classifyError(error: { code?: string; message?: string } | null): AccessResult {
  if (error === null) return { kind: 'error', reason: 'unknown' }
  if (error.code !== undefined && DENIED_CODES.has(error.code)) return { kind: 'denied' }
  if (typeof error.message === 'string' && /failed to fetch|network/i.test(error.message)) {
    return { kind: 'error', reason: 'network' }
  }
  return { kind: 'error', reason: 'unknown' }
}

function toRole(value: string): OrganisationRole {
  // Anything unrecognised is treated as the least privileged role rather than
  // rejected, so a future role added server-side degrades to read-only instead
  // of locking a legitimate user out. The database still enforces the truth.
  return value === 'owner' || value === 'admin' ? value : 'viewer'
}

/**
 * Loads the user's profile, active memberships and platform-admin status.
 *
 * `userId` comes from the verified session, never from a route parameter, a
 * query string, localStorage or a form. Callers cannot ask about somebody else:
 * the policies would refuse, and this function has no parameter for it.
 */
export async function loadAccess(userId: string): Promise<AccessResult> {
  const supabase = getSupabaseClient()

  const profileResponse = await supabase
    .from('profiles')
    .select('id, email, full_name')
    .eq('id', userId)
    .maybeSingle()

  if (profileResponse.error !== null) return classifyError(profileResponse.error)
  if (profileResponse.data === null) return { kind: 'incomplete' }

  const membershipResponse = await supabase
    .from('organisation_memberships')
    .select('id, organisation_id, role, can_view_financials, employee_id, organisations(id, name, status)')
    .eq('user_id', userId)
    .eq('status', 'active')

  if (membershipResponse.error !== null) return classifyError(membershipResponse.error)

  // A user who is not a platform administrator reads zero rows here, because
  // the policy filters them out. That is an expected empty result, not a denial.
  const platformResponse = await supabase.from('platform_admins').select('user_id').eq('user_id', userId)

  if (platformResponse.error !== null) return classifyError(platformResponse.error)

  const rows = (membershipResponse.data ?? []) as unknown as MembershipRow[]
  const memberships: AccessMembership[] = rows
    // A membership whose organisation did not come back is one RLS hid, which
    // means it is not usable. Dropping it is correct; inventing a placeholder
    // name would put a tenant on screen the user cannot actually read.
    .filter((row) => row.organisations !== null)
    .map((row) => ({
      membershipId: row.id,
      organisationId: row.organisation_id,
      organisationName: row.organisations!.name,
      organisationStatus: row.organisations!.status === 'suspended' ? 'suspended' : 'active',
      role: toRole(row.role),
      canViewFinancials: row.can_view_financials === true,
      /*
       * Which employee this login is, or null.
       *
       * Read from the membership row rather than worked out later, because it
       * is the same fact RLS reads through `app_private.linked_employee_id`.
       * One authority, so the screen and the policy can never disagree about
       * who somebody is. Null is ordinary: an office administrator is a user
       * and not an operational worker (ADR-109).
       */
      employeeId: row.employee_id,
    }))

  return {
    kind: 'ok',
    access: {
      profile: {
        userId: profileResponse.data.id as string,
        email: profileResponse.data.email as string,
        fullName: (profileResponse.data.full_name as string | null) ?? null,
      },
      memberships,
      isPlatformAdmin: (platformResponse.data ?? []).length > 0,
    },
  }
}
